
/*
 * VNCcontact+ : A new level of contact management
 * Copyright (C) 2015-2020 VNC – Virtual Network Consult AG (info@vnc.biz)
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, version 3 of the License.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. Look for COPYING file in the top folder.
 * If not, see http://www.gnu.org/licenses/.
 */

import {
    Component, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy,
    NgZone, AfterViewInit, HostListener } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { BroadcastKeys } from "src/app/common/enums/broadcast.enum";
import { Subject } from "rxjs";
import { debounceTime, take, takeUntil } from "rxjs/operators";
import { Broadcaster } from "src/app/common/providers/broadcaster.service";
import { FormControl } from "@angular/forms";
import { ColorControlDialogComponent } from "../color-control-dialog/color-control-dialog.component";
import { ContactRootState, getContactTags } from "../../store";
import { ContactTag } from "../../models/contact-tag.model";
import { getOnlineStatus, RootState } from "src/app/reducers";
import { Store } from "@ngrx/store";
import { ContactService } from "src/app/common/service/contact-service";
import { ToastService } from "src/app/common/service/tost.service";
import { TranslateService } from "@ngx-translate/core";

@Component({
    selector: "vp-create-tag-dialog",
    templateUrl: "./create-contact-tag.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CreateTagComponent implements OnDestroy, AfterViewInit {

    tagTitle: string = "";
    clientWidth;
    tagColor: string = "#20ae80";
    private isAlive$ = new Subject<boolean>();
    tagName: FormControl = new FormControl("", []);
    contactTags: ContactTag[] = [];
    isOnline: boolean = true;
    constructor(
        private dialogRef: MatDialogRef<CreateTagComponent>,
        private changeDetectionRef: ChangeDetectorRef,
        private broadcaster: Broadcaster,
        private ngZone: NgZone,
        private matDialog: MatDialog,
        private contactService: ContactService,
        private toastService: ToastService,
        private translate: TranslateService,
        private store: Store<RootState | ContactRootState>,
    ) {

        this.broadcaster.on<any>(BroadcastKeys.HIDE_CREATE_TAG_DIALOG).pipe(takeUntil(this.isAlive$)).subscribe(res => {
            this.ngZone.run(() => {
                this.close();
            });
        });

        console.log("[CreateTagComponent]");
        setTimeout(() => {
            this.changeDetectionRef.detectChanges();
        }, 50);
        this.onResize();
        this.tagName.valueChanges.pipe(debounceTime(300), takeUntil(this.isAlive$)).subscribe(value => {
            this.searchFromTags();
        });
        this.store.select(getOnlineStatus).pipe(takeUntil(this.isAlive$)).subscribe(res => {
            this.isOnline = res;
            this.changeDetectionRef.markForCheck();
        });
    }

    @HostListener("window:resize", ["$event"])
    onResize() {
        console.log("window:resize event", window.innerWidth);
        this.clientWidth = window.innerWidth;
    }

    closeDialog(): void {
        this.close();
    }

    ngOnDestroy() {
        this.isAlive$.next(false);
        this.isAlive$.complete();
    }

    ngAfterViewInit() {
        this.changeDetectionRef.detectChanges();
    }

    close(): void {
        this.dialogRef.close();
    }

    createTag(): void {
        let isDuplicate = false;
        this.contactTags.forEach(tag => {
            if (tag.name === this.tagName?.value) {
                isDuplicate = true;
            }
        });
        if (isDuplicate) {
            this.translate.get("DUPLICATE_TAG_NAME").pipe(take(1)).subscribe((text: string) => {
                this.toastService.showPlainMessage(text, 2000, true);
            });
            return;
        }
        this.dialogRef.close({ tag: this.tagName?.value, color: this.tagColor });
    }

    isValidTagNameNgmodule(): boolean {
        if ((this.tagName.value.length > 0) && (this.tagName.value.indexOf("\"") === -1)) {
            return true;
        } else {
            return false;
        }
    }

    openColorDialog(){
        const changeColorDialogRef = this.matDialog.open(ColorControlDialogComponent, {
        height: "auto",
        maxHeight: "70%",
        width: "99%",
        maxWidth: "244px",
        autoFocus: false,
        panelClass: "vp-color-control-dialog",
        data: { folderColor : this.tagColor }
        });
        changeColorDialogRef.afterClosed().subscribe(operation => {
            if (operation.selectedColor) {
                this.tagColor = operation.selectedColor;
            }
        });
    }

    updateDefaultColor(event) {
        this.tagColor = event.value;
        this.changeDetectionRef.markForCheck();
    }

    searchFromTags(): void {
        if (this.tagName.value !== "") {
            if (this.isOnline) {
                this.contactService.getSearchTags(this.tagName.value).subscribe(tag => {
                    if (tag && tag.tags.length > 0) {
                        this.contactTags = tag?.tags;
                    } else {
                        this.contactTags = [];
                    }
                });
            } else {
                this.contactTags = this.contactTags.filter(tag => tag.name.toLowerCase().includes(this.tagName.value.toLowerCase()));
            }
            this.changeDetectionRef.markForCheck();
        } else {
            this.store.select(getContactTags).pipe(take(1)).subscribe((contactTags: ContactTag[]) => {
                this.contactTags = contactTags;
                this.changeDetectionRef.markForCheck();
            });
        }
    }
}
